import { FloppyDiskIcon } from '@phosphor-icons/react';
import { format } from 'date-fns/format';
import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { deepDifference } from '@/helpers';
import { PageHeader, Spinner } from '@/modules/common';
import { Routes } from '@/router/routes';

import { useModelDetails } from '../api';
import { ModelForm } from '../components';
import { UpdateModelBody } from '../types';

export const ModelDetailsPage: FC = () => {
  const params = useParams<{ id: string }>();
  const {
    details,
    uploadUrlData,
    isFetching,
    optionsData,
    refetchUploadUrl,
    updateModelMutation,
  } = useModelDetails(params.id);

  const defaultValues = useMemo(() => {
    if (!details) return null;

    return {
      name: details.name,
      colorId: details.color?.id,
      typeId: details.type?.id,
      genderId: details.gender?.id,
      fileNameId: details.fileName?.id,
      updatedAt: format(new Date(details.updatedAt), 'yyyy-MM-dd'),
      status: details.status,
      publicUrl: details.publicUrl,
    };
  }, [details]);

  const updateModel = async (data) => {
    if (uploadUrlData?.modelId) {
      try {
        const body = deepDifference<UpdateModelBody>(data, defaultValues);
        await updateModelMutation.mutateAsync({
          ...body,
          id: params.id,
        });
        toast.success(`Model “${data.name}” updated successfully!`);
      } catch (error) {
        console.error(error.message);
      }
    }
  };

  const refetchUploadCredentials = async () => {
    try {
      await refetchUploadUrl();
    } catch (error) {
      toast.error('Uh oh! Something went wrong.');
    }
  };

  if (isFetching) {
    return <Spinner />;
  }

  return (
    <ModelForm
      editMode
      onSubmit={updateModel}
      options={optionsData}
      defaultValues={defaultValues}
      uploadUrlData={uploadUrlData}
      modelDetails={details}
      refetchUploadCredentials={refetchUploadCredentials}
    >
      {({ isLoading }) => (
        <PageHeader
          title={details?.name}
          asChildPage
          backPath={Routes.Models}
          actions={[
            <Button
              key="save-model"
              type="submit"
              data-testid="save-model-button"
              isLoading={
                isFetching || isLoading || updateModelMutation.isPending
              }
              startIcon={<FloppyDiskIcon />}
            >
              Save Changes
            </Button>,
          ]}
        />
      )}
    </ModelForm>
  );
};
